import { isServer } from "@tanstack/query-core";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import z from "zod";

import { title } from "@/components/base/primitives";

import type { FieldProps } from "../shared";

import "./container.css";
import usaepayStyles from "./styles.css?inline";

type PaymentCardConfig = {
  styles: string;
};

declare class PaymentCard extends EventTarget {
  constructor();

  generateHTML(config: PaymentCardConfig): void;
  addHTML(targetId: string): void;
}

type GetPaymentKeyResult =
  | {
      key: string;
      type: "payment_key";
      creditcard: {
        number: string;
        cardtype: string;
      };
      error?: undefined;
    }
  | { key?: undefined; error: { message: string } };

declare class UsaepayClient {
  constructor(publicKey: string);

  createPaymentCardEntry(): PaymentCard;
  getPaymentKey(card: PaymentCard): Promise<GetPaymentKeyResult>;
}

interface Usaepay {
  Client: typeof UsaepayClient;
}

declare global {
  interface Window {
    usaepay: Usaepay;
  }
}

const paymentCardConfig = {
  display_labels: true,
  labels: {
    cnum: "Card Number",
    exp: "Expiration Date",
    cvv: "Security Code",
  },
  label_position: "top", // or 'bottom'
  placeholders: {
    cnum: "1234 5678 9012 3456",
    exp: "MM/YY",
    cvv: "123",
  },
  styles: usaepayStyles,
  display_errors: true,
};

export const paymentCardSchema = z.object({
  cnum: z.boolean().refine((value) => value, {
    error: "Enter a valid credit number.",
  }),
  cvv: z.boolean().refine((value) => value, {
    error: "Enter a valid security code.",
  }),
  exp: z.boolean().refine((value) => value, {
    error: "Enter a valid expiration date.",
  }),
});

export function useUsaePay() {
  const [state, setState] = useState<{
    client: UsaepayClient;
    paymentCard: PaymentCard;
  } | null>(() => {
    if (isServer || !window.usaepay) return null;
    const client = new window.usaepay.Client(
      import.meta.env.VITE_USAEPAY_PUBLIC_KEY!,
    );
    return { client, paymentCard: client.createPaymentCardEntry() };
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (isServer || stateRef.current) return;

    const script = document.querySelector<HTMLScriptElement>(
      'script[src*="pay.js"]',
    );
    if (!script) return;

    function init() {
      if (stateRef.current || !window.usaepay) return;
      const client = new window.usaepay.Client(
        import.meta.env.VITE_USAEPAY_PUBLIC_KEY!,
      );
      setState({ client, paymentCard: client.createPaymentCardEntry() });
    }

    script.addEventListener("load", init);
    return () => script.removeEventListener("load", init);
  }, []);

  const { mutateAsync: getPaymentKey, failureReason } = useMutation({
    mutationFn: async () => {
      if (!state) throw new Error("Payment system not ready");
      return state.client.getPaymentKey(state.paymentCard);
    },
  });

  return {
    paymentCard: state?.paymentCard,
    ready: !!state,
    failureReason,
    getPaymentKey,
  };
}

type PaymentKeyFieldProps = FieldProps & {
  className?: string;
  paymentCard: PaymentCard | undefined;
};

export function PaymentKeyField({
  className,
  paymentCard,
  field,
}: PaymentKeyFieldProps) {
  const fieldRef = useRef(field);
  fieldRef.current = field;

  useEffect(() => {
    if (!paymentCard) return;

    paymentCard.generateHTML(paymentCardConfig);
    paymentCard.addHTML("paymentCardContainer");

    const handleError = (err: unknown) => {
      console.log("paymentCard.error", err);
    };

    const handleValid = (key: "cnum" | "cvv" | "exp") => {
      fieldRef.current.setValue((value) => ({
        ...value,
        [key]: true,
      }));
      fieldRef.current.handleBlur();
    };

    const handleInvalid = (key: "cnum" | "cvv" | "exp") => {
      fieldRef.current.setValue((value) => ({
        ...value,
        [key]: false,
      }));
    };

    paymentCard.addEventListener("error", handleError);
    paymentCard.addEventListener("valid", handleValid);
    paymentCard.addEventListener("invalid", handleInvalid);
  }, [paymentCard]);

  return (
    <div id="paymentForm" className={className}>
      {/* {failureReason && ( */}
      {/* 	<Alert title="Uh oh" description={failureReason.toString()} /> */}
      {/* )} */}

      <div className="flex flex-col gap-y-2">
        <h2 className={title({ size: "xs", class: "mb-2 col-span-full" })}>
          Payment Information
        </h2>

        <div id="paymentCardContainer" />
        <div id="paymentCardErrorContainer" />
      </div>
    </div>
  );
}
